import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "../context/CartContext";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight, AlertTriangle, CheckCircle2, Lock } from "lucide-react";

export default function CartPage() {
  const { t } = useTranslation();
  const { items, updateQty, removeItem, subtotal, surchargeTotal, lineSurcharge } = useCart();
  const navigate = useNavigate();
  const [acknowledgedHalf, setAcknowledgedHalf] = useState(false);
  const vat = subtotal * 0.21;
  const shipping = subtotal > 500 || subtotal === 0 ? 0 : 19.95;
  const total = subtotal + vat + shipping;

  // Aggregate kit types
  const halfCount = items.filter((i) => i.is_half).reduce((s, i) => s + i.quantity, 0);
  const setCount = items.filter((i) => !i.is_half && i.kit_label_nl).reduce((s, i) => s + i.quantity, 0);
  const requiresAcknowledge = halfCount > 0;
  const canCheckout = !requiresAcknowledge || acknowledgedHalf;

  // Logistics totals — total order weight + estimated box count.
  // Items can share a single box as long as the combined weight stays
  // ≤ 20 kg. So total boxes = ceil(totalWeightKg / 20).
  // Example: 6.94 kg → 1 box. 25 kg → 2 boxes. 41 kg → 3 boxes.
  const BOX_MAX_KG = 20;
  const totalWeightKg = items.reduce((s, i) => {
    const w = Number(i.meta?.weight_g || 0);
    return s + (w * i.quantity) / 1000;
  }, 0);
  const totalBoxes = totalWeightKg > 0 ? Math.ceil(totalWeightKg / BOX_MAX_KG) : 0;

  const handleCheckout = () => {
    if (!canCheckout) return;
    navigate("/checkout");
  };

  return (
    <div className="min-h-screen bg-white" data-testid="cart-page">
      <div className="mx-auto max-w-[1400px] px-5 py-10 lg:px-10">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <span className="label-uppercase">{t("cart.title")}</span>
          <Link
            to="/bulk-order"
            data-testid="cart-bulk-order-link"
            className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-nibro-sage-dark transition hover:border-nibro-sage hover:bg-nibro-sage-pale"
          >
            {t("cart.bulk_csv")} <ArrowRight size={11} />
          </Link>
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-zinc-900 sm:text-5xl">
          {items.length === 0 ? t("cart.empty") : t("cart.items_ready", { count: items.length })}
        </h1>

        {items.length === 0 ? (
          <div className="mt-10 rounded-md border border-zinc-200 bg-white p-12 text-center">
            <ShoppingBag size={36} className="mx-auto text-zinc-300" />
            <p className="mt-4 text-zinc-500">{t("cart.empty")}</p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/products"
                className="inline-flex items-center gap-2 rounded-md bg-nibro-sage px-6 py-3 text-sm font-bold text-white transition hover:bg-nibro-sage-hover"
                data-testid="cart-browse-products"
              >
                Bekijk producten <ArrowRight size={14} />
              </Link>
              <Link
                to="/bulk-order"
                className="inline-flex items-center gap-2 rounded-md border border-nibro-sage-dark bg-white px-6 py-3 text-sm font-bold text-nibro-sage-dark transition hover:bg-nibro-sage-pale"
                data-testid="cart-empty-bulk-link"
              >
                Of plak een SKU-lijst (bulk)
              </Link>
            </div>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_380px]">
            <div className="space-y-3">
              {/* Kit summary banner */}
              {halfCount > 0 && (
                <div
                  className="flex items-start gap-3 rounded-md border-2 border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                  data-testid="cart-half-warning"
                >
                  <AlertTriangle size={18} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-display text-sm font-bold uppercase tracking-wide">
                      Let op — {halfCount} halve beuge{halfCount === 1 ? "l" : "ls"} in winkelwagen
                    </div>
                    <div className="mt-1 leading-relaxed">
                      U koopt losse halve beugels (1 stuk per artikel). Voor een volledige montage heeft u
                      <strong> 2 helften (boven + onder)</strong> plus
                      <strong> bouten en moeren</strong> nodig per pijpaansluiting.
                      Indien u een complete set wilt: kies "pijpbeugelset" in plaats van "halve pijpbeugel".
                    </div>
                  </div>
                </div>
              )}
              {setCount > 0 && halfCount === 0 && (
                <div
                  className="flex items-center gap-3 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800"
                  data-testid="cart-set-info"
                >
                  <CheckCircle2 size={16} className="flex-shrink-0" />
                  <div>
                    <strong>{setCount} complete set{setCount === 1 ? "" : "s"}</strong> in winkelwagen — inclusief 2 helften, bouten en moeren per set.
                  </div>
                </div>
              )}
              {items.map((it) => (
                <div
                  key={it.lineId}
                  className="rounded-md border border-zinc-200 bg-white"
                  data-testid={`cart-item-${it.lineId}`}
                >
                  {/* Kit-type warning bar */}
                  {it.is_half && (
                    <div className="flex items-start gap-2 rounded-t border-b border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-900">
                      <Trash2 size={0} aria-hidden />
                      <strong className="font-mono uppercase tracking-wider text-amber-800">Let op:</strong>
                      <span>Halve beugel — 1 helft per stuk. Bouten/moeren niet inbegrepen.</span>
                    </div>
                  )}
                  {!it.is_half && it.kit_label_nl && (
                    <div className="flex items-center gap-2 rounded-t border-b border-emerald-200 bg-emerald-50 px-4 py-1.5 text-[11px] text-emerald-800">
                      <span className="font-mono uppercase tracking-wider">✓ {it.kit_label_nl}</span>
                    </div>
                  )}
                  <div className="flex gap-4 p-4">
                  {it.image && (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL || ""}/api/images/sku/${encodeURIComponent(it.sku)}`}
                      alt={it.name}
                      className="h-24 w-24 flex-shrink-0 rounded-md border border-zinc-100 object-contain p-1"
                      onError={(e) => {
                        // Prefer SKU-level image override; fall back to the
                        // image we cached at add-to-cart time so freshly
                        // uploaded images replace stale thumbnails without
                        // requiring the customer to re-add the item.
                        if (e.currentTarget.dataset.fallbackApplied) return;
                        e.currentTarget.dataset.fallbackApplied = "1";
                        e.currentTarget.src = it.image;
                      }}
                    />
                  )}
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                      <span>{it.sku}</span>
                      {/* Material badge — must be obvious so a B2B buyer
                          can never confuse 304 with 316 in a multi-line
                          quote. 304 = green sage, 316 = blue. */}
                      {it.material && (
                        <span
                          data-testid={`cart-item-material-${it.lineId}`}
                          className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${
                            String(it.material) === "316"
                              ? "border-sky-300 bg-sky-50 text-sky-800"
                              : String(it.material) === "304"
                                ? "border-nibro-sage bg-nibro-sage-pale text-nibro-sage-dark"
                                : "border-zinc-300 bg-zinc-50 text-zinc-700"
                          }`}
                          title={
                            String(it.material) === "316"
                              ? "RVS V4A-316 (zee- en chemische omgeving)"
                              : String(it.material) === "304"
                                ? "RVS V2A-304 (standaard binnen/buiten)"
                                : it.material
                          }
                        >
                          {String(it.material) === "316"
                            ? "V4A · 316"
                            : String(it.material) === "304"
                              ? "V2A · 304"
                              : String(it.material).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="font-display font-bold leading-tight text-zinc-900">{it.name}</div>
                    {it.meta && (
                      <div className="mt-1 font-mono text-xs text-zinc-500">
                        Ø {it.meta.diameter_mm} mm{it.meta.dn ? ` · DN${it.meta.dn}` : ""}{it.meta.inch ? ` · ${it.meta.inch}` : ""}{it.meta.strip_size ? ` · ${it.meta.strip_size}` : ""}{it.meta.weight_g ? ` · ${it.meta.weight_g} g/stuk` : ""}
                      </div>
                    )}
                    {/* Split-pack surcharge warning — appears whenever the
                        ordered quantity is NOT a whole multiple of the
                        variant's pack_size (Nibro: VE=10 + 8 stuks OR
                        VE=10 + 12 stuks both trigger the +€24.50 fee). */}
                    {(() => {
                      const ps = Number(it.meta?.pack_size || 0);
                      const fee = lineSurcharge(it);
                      if (!ps || ps <= 1 || fee <= 0) return null;
                      // Closest VE-multiples below and above the current qty
                      const roundDownVe = Math.floor(it.quantity / ps) * ps;
                      const roundUpVe = roundDownVe + ps;
                      const isSubpack = it.quantity < ps;
                      const label = isSubpack
                        ? `Onder verpakkingseenheid (${ps} stuks)`
                        : `Geen volledige verpakking (${roundDownVe} + ${it.quantity - roundDownVe} los) — toeslag +€${fee.toFixed(2)}`;
                      return (
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]" data-testid={`cart-subpack-warning-${it.lineId}`}>
                          <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 font-bold text-amber-800">
                            <AlertTriangle size={11} /> {isSubpack ? `${label} — toeslag +€${fee.toFixed(2)}` : label}
                          </span>
                          {roundDownVe >= ps && (
                            <button
                              type="button"
                              onClick={() => updateQty(it.lineId, roundDownVe)}
                              data-testid={`cart-bump-down-pack-${it.lineId}`}
                              className="inline-flex items-center gap-1 rounded-full border border-zinc-300 bg-white px-2.5 py-0.5 font-bold text-zinc-700 transition hover:bg-zinc-50"
                              title={`${roundDownVe} stuks — toeslag vervalt`}
                            >
                              ↓ Rond af op {roundDownVe}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => updateQty(it.lineId, roundUpVe)}
                            data-testid={`cart-bump-up-pack-${it.lineId}`}
                            className="inline-flex items-center gap-1 rounded-full border border-aramfix-red bg-white px-2.5 py-0.5 font-bold text-aramfix-red transition hover:bg-aramfix-red/5"
                            title={`${roundUpVe} stuks — toeslag vervalt`}
                          >
                            ↑ Rond op naar {roundUpVe} (toeslag vervalt)
                          </button>
                        </div>
                      );
                    })()}
                    {/* Omdoos-korting tier indicator + rounding helpers */}
                    {it.meta?.box_qty > 0 && (() => {
                      const bq = it.meta.box_qty;
                      const aligned = it.quantity >= bq && it.quantity % bq === 0;
                      const remainder = it.quantity % bq;
                      const roundUp = it.quantity < bq ? bq : it.quantity + (bq - remainder);
                      const roundDown = Math.floor(it.quantity / bq) * bq;   // 0 when qty < bq
                      const pct = (it.meta.box_discount_pct || 10).toFixed(0);
                      return (
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]" data-testid={`cart-pricing-tier-${it.lineId}`}>
                          <span className="font-mono text-zinc-500">€{it.unit_price.toFixed(2)} / stuk</span>
                          {aligned ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-bold text-emerald-700">
                              ✓ Omdoos-korting (-{pct}%)
                            </span>
                          ) : (
                            <>
                              <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 font-bold text-zinc-600">
                                Stukprijs
                              </span>
                              <button
                                type="button"
                                onClick={() => updateQty(it.lineId, roundUp)}
                                data-testid={`cart-upgrade-bundle-${it.lineId}`}
                                className="inline-flex items-center gap-1 rounded-full border border-aramfix-red bg-white px-2.5 py-0.5 font-bold text-aramfix-red transition hover:bg-aramfix-red/5"
                                title={`Rond omhoog naar volle omdoos van ${roundUp} stuks — korting ${pct}% actief`}
                              >
                                ↑ Rond op naar {roundUp} ({roundUp - it.quantity > 0 ? `+${roundUp - it.quantity}` : "–"} · −{pct}%)
                              </button>
                              {roundDown >= bq && (
                                <button
                                  type="button"
                                  onClick={() => updateQty(it.lineId, roundDown)}
                                  data-testid={`cart-downround-bundle-${it.lineId}`}
                                  className="inline-flex items-center gap-1 rounded-full border border-zinc-300 bg-white px-2.5 py-0.5 font-bold text-zinc-700 transition hover:bg-zinc-50"
                                  title={`Rond af naar ${roundDown} stuks (volle omdoos) — korting ${pct}% actief`}
                                >
                                  ↓ Rond af naar {roundDown} (−{it.quantity - roundDown} · −{pct}%)
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })()}
                    {it.config && (
                      <div className="mt-1 text-xs text-zinc-500">
                        Ø{it.config.diameter_mm}mm · {it.config.strip_width}x{it.config.strip_thickness}mm · {it.config.material.toUpperCase()} · {it.config.finish}
                      </div>
                    )}
                    <div className="mt-auto flex items-center justify-between pt-3">
                      <div className="flex items-center rounded-md border border-zinc-200">
                        <button type="button" onClick={() => updateQty(it.lineId, it.quantity - 1)} className="p-2 text-zinc-500 hover:text-zinc-900" data-testid={`cart-qty-dec-${it.lineId}`}><Minus size={12} /></button>
                        <input type="number" value={it.quantity} onChange={(e) => updateQty(it.lineId, parseInt(e.target.value) || 1)} className="w-12 bg-transparent text-center font-mono text-sm text-zinc-900 focus:outline-none" />
                        <button type="button" onClick={() => updateQty(it.lineId, it.quantity + 1)} className="p-2 text-zinc-500 hover:text-zinc-900" data-testid={`cart-qty-inc-${it.lineId}`}><Plus size={12} /></button>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-display text-lg font-bold text-zinc-900" data-testid={`cart-line-total-${it.lineId}`}>
                          €{(it.unit_price * it.quantity + lineSurcharge(it)).toFixed(2)}
                        </span>
                        <button type="button" onClick={() => removeItem(it.lineId)} className="rounded-md p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-aramfix-red" data-testid={`cart-remove-${it.lineId}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="h-fit space-y-4 rounded-md border-2 border-nibro-sage bg-white p-6">
              <div className="label-uppercase">{t("checkout.order_summary")}</div>
              <Line label={t("cart.subtotal_ex_vat")} value={`€${subtotal.toFixed(2)}`} />
              {surchargeTotal > 0 && (
                <Line
                  label={t("cart.split_pack_surcharge")}
                  value={`+€${surchargeTotal.toFixed(2)}`}
                  testid="cart-summary-subpack-surcharge"
                />
              )}
              <Line label={t("cart.vat_label")} value={`€${vat.toFixed(2)}`} />
              <Line label={`${t("cart.shipping")}${shipping === 0 ? " (gratis)" : ""}`} value={shipping === 0 ? "€0.00" : `€${shipping.toFixed(2)}`} />
              <div className="border-t border-zinc-100 pt-4">
                <div className="flex items-baseline justify-between">
                  <span className="font-display text-base font-bold">{t("cart.total")}</span>
                  <span className="font-display text-2xl font-bold text-nibro-sage-dark">€{total.toFixed(2)}</span>
                </div>
              </div>

              {/* Logistics summary — total weight + estimated box count so
                  the buyer can validate against shipping lane / pallet plan
                  before checkout. Hidden when no line has a known weight
                  (e.g. cart only contains price-on-request items). */}
              {(totalWeightKg > 0 || totalBoxes > 0) && (
                <div className="mt-3 rounded-md border border-zinc-200 bg-zinc-50 p-3" data-testid="cart-logistics-summary">
                  <div className="mb-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Logistiek
                  </div>
                  <div className="space-y-1 text-sm">
                    {totalWeightKg > 0 && (
                      <div className="flex items-baseline justify-between" data-testid="cart-total-weight">
                        <span className="text-zinc-600">{t("cart.total_weight")}</span>
                        <span className="font-mono font-bold text-zinc-900">
                          {totalWeightKg.toFixed(2)} kg
                        </span>
                      </div>
                    )}
                    {totalBoxes > 0 && (
                      <div className="flex items-baseline justify-between" data-testid="cart-total-boxes">
                        <span className="text-zinc-600">{t("cart.total_boxes")} (≤20 kg)</span>
                        <span className="font-mono font-bold text-zinc-900">
                          {totalBoxes}× omdoos
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {requiresAcknowledge && (
                <label
                  className={`mt-2 flex cursor-pointer items-start gap-2 rounded-md border-2 px-3 py-3 text-xs transition ${
                    acknowledgedHalf
                      ? "border-emerald-400 bg-emerald-50 text-emerald-900"
                      : "border-amber-400 bg-amber-50 text-amber-900"
                  }`}
                  data-testid="half-acknowledge-label"
                >
                  <input
                    type="checkbox"
                    checked={acknowledgedHalf}
                    onChange={(e) => setAcknowledgedHalf(e.target.checked)}
                    className="mt-0.5 h-4 w-4 flex-shrink-0 accent-nibro-sage"
                    data-testid="half-acknowledge-checkbox"
                  />
                  <span className="leading-relaxed">
                    <strong>Ik bevestig</strong> dat ik bewust losse <strong>halve beugels</strong> bestel (geen complete sets). Voor een volledige montage zijn 2 helften + bouten/moeren per pijp nodig.
                  </span>
                </label>
              )}
              <button
                type="button"
                onClick={handleCheckout}
                disabled={!canCheckout}
                className={`mt-2 flex w-full items-center justify-center gap-2 rounded-md px-4 py-3 text-sm font-bold text-white transition ${
                  canCheckout
                    ? "bg-nibro-sage hover:bg-nibro-sage-hover"
                    : "cursor-not-allowed bg-zinc-300"
                }`}
                data-testid="checkout-button"
                aria-disabled={!canCheckout}
              >
                {canCheckout ? (
                  <>{t("cart.checkout_cta")} <ArrowRight size={14} /></>
                ) : (
                  <><Lock size={14} /> Bevestig halve beugels om af te rekenen</>
                )}
              </button>
              <p className="text-center text-xs text-zinc-500">
                {t("cart.checkout_footnote")}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Line({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between text-sm" data-testid={testid}>
      <span className="text-zinc-600">{label}</span>
      <span className="font-mono text-zinc-900">{value}</span>
    </div>
  );
}
